import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Crown, Check, Loader2, Sparkles, MessageSquare, Zap, Shield } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { API } from "../App";

const Subscriptions = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);
  const [currentPlan, setCurrentPlan] = useState("free");

  useEffect(() => {
    if (!user) {
      navigate("/");
      return;
    }
    fetchPlans();
    setCurrentPlan(user?.subscription_plan || "free");
  }, [user, navigate]);

  const fetchPlans = async () => {
    try {
      const response = await fetch(`${API}/subscriptions/plans`);
      if (response.ok) {
        const data = await response.json();
        setPlans(data.plans || {});
      }
    } catch (error) {
      console.error("Error fetching plans:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planKey) => {
    setSubscribing(planKey);
    try {
      const response = await fetch(`${API}/subscriptions/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ plan: planKey }),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Subscribed to ${data.plan} plan!`, {
          description: "Your message limit has been reset.",
        });
        setCurrentPlan(planKey);
        
        // Update user state
        if (setUser && user) {
          setUser({
            ...user,
            subscription_plan: planKey,
            messages_used: 0,
          });
        }
      } else {
        throw new Error("Subscription failed");
      }
    } catch (error) {
      toast.error("Failed to subscribe. Please try again.");
    } finally {
      setSubscribing(null);
    }
  };

  const planData = {
    free: {
      name: "Free",
      price: 0,
      messages: 10,
      features: [
        "10 AI consultations",
        "Basic symptom analysis",
        "Nearby hospital finder",
        "Email support",
      ],
    },
    starter: {
      name: "Starter",
      price: 29,
      messages: 100,
      features: [
        "100 AI consultations/month",
        "Advanced triage analysis",
        "Priority booking",
        "Voice input enabled",
        "Chat history saved",
      ],
    },
    pro: {
      name: "Pro",
      price: 49,
      messages: 1000,
      features: [
        "1000 AI consultations/month",
        "Comprehensive health analysis",
        "Priority booking",
        "Voice input enabled",
        "Chat history saved",
        "Family member profiles",
        "Priority support",
      ],
      recommended: true,
    },
  };

  return (
    <div className="min-h-screen pb-24 bg-gradient-to-br from-blue-50 via-white to-blue-100">
      {/* Header */}
      <div className="glass-panel sticky top-0 z-40 px-4 py-4 border-b border-white/30">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-amber-500 rounded-xl flex items-center justify-center">
              <Crown className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-heading font-semibold text-slate-800">Subscription</h1>
              <p className="text-xs text-slate-500">Choose your plan</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Current Plan Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-panel rounded-2xl p-4 mb-6 flex items-center gap-4"
        >
          <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
            <Zap className="w-6 h-6 text-primary-500" />
          </div>
          <div className="flex-1">
            <p className="text-sm text-slate-500">Current Plan</p>
            <p className="font-heading font-semibold text-slate-800 capitalize">{currentPlan}</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-primary-600">
              ₹{planData[currentPlan]?.price || 0}
              <span className="text-sm font-normal text-slate-500">/mo</span>
            </p>
          </div>
        </motion.div>

        {/* Plans */}
        <div className="space-y-4">
          {Object.entries(planData).map(([key, plan], index) => (
            <motion.div
              key={key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`glass-card rounded-3xl p-6 relative ${
                plan.recommended ? "border-2 border-primary-500 subscription-recommended" : ""
              }`}
              data-testid={`plan-${key}`}
            >
              {plan.recommended && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-primary-500 text-white text-xs px-4 py-1 rounded-full font-medium flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    Recommended
                  </span>
                </div>
              )}

              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-heading text-xl font-semibold text-slate-800">{plan.name}</h3>
                  <p className="text-sm text-slate-500">{plan.messages} messages/month</p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-primary-600">
                    ₹{plan.price}
                  </p>
                  <p className="text-xs text-slate-500">/month</p>
                </div>
              </div>

              <ul className="space-y-2 mb-6">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                    <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-green-600" />
                    </div>
                    {feature}
                  </li>
                ))}
              </ul>

              {currentPlan === key ? (
                <Button
                  className="w-full rounded-full bg-slate-200 text-slate-600 cursor-default"
                  disabled
                >
                  Current Plan
                </Button>
              ) : key === "free" ? (
                <Button
                  variant="outline"
                  className="w-full rounded-full"
                  disabled
                >
                  Free Forever
                </Button>
              ) : (
                <Button
                  className={`w-full rounded-full ${
                    plan.recommended
                      ? "bg-primary-500 hover:bg-primary-600"
                      : "bg-slate-800 hover:bg-slate-900"
                  }`}
                  onClick={() => handleSubscribe(key)}
                  disabled={subscribing === key}
                  data-testid={`subscribe-${key}-btn`}
                >
                  {subscribing === key ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : null}
                  Subscribe to {plan.name}
                </Button>
              )}
            </motion.div>
          ))}
        </div>

        {/* Trust Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-8 text-center"
        >
          <div className="flex items-center justify-center gap-6 text-sm text-slate-500">
            <div className="flex items-center gap-1">
              <Shield className="w-4 h-4 text-green-500" />
              <span>Secure Payment</span>
            </div>
            <div className="flex items-center gap-1">
              <MessageSquare className="w-4 h-4 text-primary-500" />
              <span>Cancel Anytime</span>
            </div>
          </div>
          <p className="text-xs text-slate-400 mt-4">
            Powered by Razorpay. All payments are secure and encrypted.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default Subscriptions;
